import { setSearch } from "@/tracker/shared/global";
import { getFilters } from "@/utils/getFilters";
import { memorizeFn } from "@/utils/memorizeFn";
import { isArray } from "lodash";
import searchTracker from "../tracker/report/searchTracker";
import { gdOssUrl } from "../utils/imageUrl";
import request from "../utils/request";

/**
 * 搜索联想词接口
 * @param {{
 *  q: string,
 *  limit?: number | 10
 * }} query
 * @param query.q 搜索词
 * @param query.limit 返回最多的结果数量
 * @returns {Promise<{result:string[], total:number, material_count: number}>}
 */
export async function getAssociate(query) {
  return request({
    url: "/search/hint",
    query: { limit: 10, ...query },
  });
}

/**
 * 搜索采集
 * @param {{
 *  q: string,
 *  colors?: string
 *  sort?: string,
 *  category?: number
 *  per_page?: number
 *  page?: number
 * }} query
 * @returns {Promise<{pins:IPin[], total:number, relations?: {
 *  word: string,
 * }[]}>}
 */
export async function getSearchPins(query) {
  const result = await request({
    url: "/search",
    query,
  });

  if (query.page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: query.q,
      search_result_number: result.pin_count || result.facets?.total || 0,
      sort_type: query.sort,
      colors: query.colors,
    });
  }

  return result;
}
// 获取搜索采集筛选器
export async function getSearchPinsFilterByQingzhou(query) {
  return request({
    url: "/search/filter",
    query,
  });
}

/**
 * 通过筛选器搜索采集
 * @param {{
 *  text?: string;
 *  colors?: string;
 * sort?: string;
 * filter_ids?: string[];
 * page: number;
 * limit?: number;
 * ignoreSearchTracker?: boolean; // 是否忽略搜索埋点
 * position?: string; // 位置,搜索结果页_采集  search_pin 搜索结果页_花瓣素材  search_material 主题页 topics
 * all_match?: boolean; // 是否全匹配 默认是全匹配(true)
 * }} param0
 * @returns {Promise<{pins:IPin[], topics: ITopic[], facets: {total:number}, relations?: {word: string}[] total?:number}>}
 */
export async function getSearchPinsByQingzhou({
  colors,
  ignoreSearchTracker,
  ...query
}) {
  const result = await request({
    url: "/search/file",
    query: {
      ...query,
      fields:
        "pins:PIN|total,facets,split_words,relations,rec_topic_material,topics",
    },
  });

  if (query.page === 1 && !ignoreSearchTracker) {
    setSearch({
      filter_ids: query?.filter_ids || [],
    });

    searchTracker({
      search_word: query.text,
      search_result_number: result.total || result.facets?.total || 0,
      sort_type: query.sort,
      colors,
    });
  }

  return result;
}

/**
 * 搜索画板
 * @param {{
 *  q: string,
 *  colors?: string
 *  sort?: string,
 *  category?: number
 *  per_page?: number
 *  page?: number
 * }} query
 * @returns {Promise<{boards:IBoardInfo[], total:number}>}
 */
export async function getSearchBoards(query) {
  const result = await request({
    url: "/search/boards",
    query: {
      ...query,
      fields: "boards:BOARD|facets|board_count",
    },
  });

  if (query.page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: query.q,
      search_result_number: result.board_count || result.facets?.total || 0,
      sort_type: query.sort,
      colors: query.colors,
    });
  }

  return result;
}

/**
 * 搜索用户
 * @param {{
 *  q: string,
 *  colors?: string
 *  sort?: string,
 *  category?: number
 *  per_page?: number?
 *  page?: number
 * }} query
 * @returns {Promise<{users:[], total:number}>}
 */
export async function getSearchUsers(query) {
  return request({
    url: "/search/people",
    query,
  });
}

/**
 * 搜索我的采集
 * @param {{
 *  q: string,
 *  colors: string
 *  sort: string,
 *  category: number
 *  per_page: number
 *  page: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchMyPins(query) {
  const result = await request({
    url: "/search/self_pins",
    query,
  });

  if (query.page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: query.q,
      search_result_number: result.total_count || 0,
      sort_type: query.sort,
      colors: query.colors,
      filter_one: ["我的采集"],
    });
  }

  return result;
}

/**
 * 搜索我的画板
 * @param {{
 *  q: string,
 *  colors: string
 *  sort: string,
 *  category: number
 *  per_page: number
 *  page: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchMyBoards(query) {
  const result = await request({
    url: "/search/self_boards",
    query,
  });

  if (query.page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: query.q,
      search_result_number: result.total_count || 0,
      sort_type: query.sort,
      colors: query.colors,
      filter_one: ["我的画板"],
    });
  }

  return result;
}

/**
 * 搜索其他人采集
 * @param {{
 *  q: string,
 *  colors: string
 *  sort: string,
 *  category: number
 *  per_page: number
 *  page: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchOtherPins({ q, urlname, page, per_page }) {
  const result = await request({
    url: `/${urlname}/pins`,
    query: {
      q,
      page,
      per_page,
      fields: "pins:PIN|total,page_num,page_size",
    },
  });

  if (page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: q,
      search_result_number: result.total_count || 0,
      filter_one: ["他的采集"],
    });
  }

  return result;
}

/**
 * 搜索画板内采集
 * @param {{
 *  q: string,
 *  colors: string
 *  sort: string,
 *  category: number
 *  per_page: number
 *  page: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchBoardPins({
  q,
  user_id,
  board_id,
  page,
  per_page,
  file_type,
  colors,
  resolution,
  ...query
}) {
  const result = await request({
    url: `/users/${user_id}/pins`,
    query: {
      q,
      board_id,
      file_type,
      colors,
      resolution,
      page,
      per_page,
      ...query,
    },
  });

  if (page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: q,
      search_result_number: result.total_count || 0,
      filter_one: ["画板内采集"],
    });
  }

  return result;
}

/**
 * 搜索来源内采集
 * @param {{
 *  q: string
 *  max: number
 *  since: number
 *  limit: number
 *  domain_id: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchDomainPins({ q, per_page, page, domain_id }) {
  const result = await request({
    url: `/domains/${domain_id}/pins`,
    query: {
      q,
      per_page,
      page,
      domain_id,
    },
  });

  if (page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: q,
      search_result_number: result.total_count || 0,
      filter_one: ["来源内采集"],
    });
  }

  return result;
}

/**
 * 搜索其他人画板
 * @param {{
 *  q: string,
 *  colors: string
 *  sort: string,
 *  category: number
 *  per_page: number
 *  page: number
 * }} query
 * @returns {Promise<{result:[], total:number}>}
 */
export async function getSearchOtherBoards({ q, urlname, page, limit }) {
  const result = await request({
    url: `/${urlname}/boards`,
    query: {
      q,
      page,
      limit,
      joined: 1,
      fields: "boards:BOARD|user,total,page_num,page_size",
    },
  });

  if (page === 1) {
    // const { facets } = result;
    // facets.categories
    searchTracker({
      search_word: q,
      search_result_number: result.total_count || 0,
      filter_one: ["他的画板"],
    });
  }

  return result;
}

/**
 * @returns {Promise<{word: string, score: number}[]>}
 */
export function getSearchHotWord() {
  return request({
    url: "/search-words/hot",
    query: {
      position: "allpages_search",
    },
  });
}

/**
 * 获取增强版搜索热词（包含预览图和标签）
 * @returns {Promise<{
 *   word: string,
 *   score: number,
 *   background_url: string,
 *   icon_style: 'new' | 'hot' | null
 * }[]>}
 */
export async function getSearchHotWordEnhanced() {
  // 获取基础热词数据
  const hotWords = await getSearchHotWord();

  // 只保留前10个热搜词
  const limitedHotWords = hotWords.slice(0, 10);

  // 过滤掉没有background_url的热词，并处理OSS裁剪
  return limitedHotWords
    .filter((hotWord) => hotWord.background_url)
    .map((hotWord) => {
      // 处理OSS裁剪
      let backgroundUrl = hotWord.background_url;
      if (/dancf\.com|grocery-cdn/.test(backgroundUrl)) {
        // gaoding域名的图片使用gdOssUrl进行方形裁剪
        backgroundUrl = gdOssUrl(backgroundUrl, {
          width: 136,
          height: 136,
          scaleType: "mfit", // 等比缩放，会自动裁剪成方形
        });
      } else if (backgroundUrl?.includes("gd-hbimg.huaban.com")) {
        // 花瓣域名的图片使用imageUrl进行方形裁剪
        backgroundUrl += "_sq180";
      }

      // 处理角标，将icon_style转换为小写
      let iconStyle = null;
      if (hotWord.icon_style) {
        const style = hotWord.icon_style.toLowerCase();
        if (style === "new" || style === "hot") {
          iconStyle = style;
        }
      }

      return {
        ...hotWord,
        background_url: backgroundUrl,
        icon_style: iconStyle,
      };
    });
}

/**
 * 获取搜索占位词
 * @returns
 */
export function getSearchPlaceholder(query) {
  return request({
    query: {
      word_type: "PLACEHOLDER_WORD",
      ...query,
    },
    url: "/search-word-interventions/hit-words",
  });
}

/**
 * 筛选器预览信息(批量获取)
 * @param {{
 *   preview_limit: number
 *   filter_ids: number|string[] | string
 * }} query
 * @returns
 */
export function getTopicsPreview({ preview_limit, filter_ids }) {
  return request({
    url: "/search/filters/preview",
    query: {
      preview_limit,
      filter_ids: isArray(filter_ids) ? filter_ids.join(",") : filter_ids,
    },
  });
}

/**
 * 获取搜索采集筛选项(带缓存memorizeFn)
 */
export const getSearchPinsFilter = memorizeFn(async () => {
  try {
    const result = await getSearchPinsFilterByQingzhou();
    return getFilters(result);
  } catch (err) {
    console.error("获取素材采集筛选项失败");
    return [];
  }
});

/**
 * 获取搜索素材筛选项(带缓存memorizeFn)
 */
export const getSearchMaterialFilter = memorizeFn(async () => {
  try {
    const result = await getSearchPinsFilterByQingzhou({ type: "material" });
    return getFilters(result);
  } catch (err) {
    console.error("获取素材采集筛选项失败");
    return [];
  }
});

/**
 * 获取AI采集筛选项(带缓存memorizeFn)
 */
export const getAiFilter = memorizeFn(async () => {
  try {
    const result = await getSearchPinsFilterByQingzhou({ type: "aigc1" });
    return { rootId: result.root_id, aiFilters: getFilters(result) };
  } catch (err) {
    console.error("获取AI采集筛选项失败");
    return { serverSideFilter: [], rootId: "" };
  }
});

/**
 * 推荐搜索词
 * @param {{
 *  q: string,
 * }} query
 * @param query.q 搜索词
 * @returns {Promise<{word: string, ugc_url: string, pgc_url: string}[]>}
 */
export async function getSearchWordsRecommend(query) {
  return request({
    url: "search-words/result-recommend",
    query: {
      query: query.q,
    },
  });
}
